import {
  Material,
  POWER_GROUP_LABELS,
  Printer,
  PrinterModelList,
  type CreatePrinterBody,
  formatBRL,
  formatIsoDate,
} from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field } from '../../components/Form';
import { Modal } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, parseBrDate, parseCentavos } from '../../lib/numbers';
import { useSettings } from '../../lib/settings-api';

interface Form {
  name: string;
  model: string;
  purchaseDate: string;
  price: string;
  lifetime: string;
  low: string;
  high: string;
  maintenance: string;
  buildVolume: string;
  nozzles: string;
  multicolorUnit: string;
  multicolorSlots: string;
  enclosure: string;
  materials: Material[];
}

function fromPrinter(p: Partial<Printer>): Form {
  return {
    name: p.name ?? '',
    model: p.model ?? '',
    purchaseDate: p.purchaseDate ? formatIsoDate(p.purchaseDate) : '',
    price: centavosInput(p.purchasePriceCentavos ?? null),
    lifetime: p.lifetimeHours ? String(p.lifetimeHours) : '5000',
    low: p.powerLowWatts !== undefined ? String(p.powerLowWatts) : '',
    high: p.powerHighWatts !== undefined ? String(p.powerHighWatts) : '',
    maintenance: centavosInput(p.maintenancePerHourCentavos ?? 10),
    buildVolume: p.buildVolume ?? '',
    nozzles: p.nozzles ?? '',
    multicolorUnit: p.multicolorUnit ?? '',
    multicolorSlots: p.multicolorSlots != null ? String(p.multicolorSlots) : '',
    enclosure: p.enclosure ?? '',
    materials: p.materials ?? [],
  };
}

const int = (t: string) => (/^\d+$/.test(t.trim()) ? Number.parseInt(t, 10) : null);
const textOrNull = (t: string) => (t.trim() === '' ? null : t.trim());

/** "Nova impressora" / "Editar impressora" (Impressora-Detalhe edit modal + wizard step 3 data). */
export function PrinterDialog({
  printer,
  onClose,
  onSaved,
}: {
  printer: Printer | null;
  onClose: () => void;
  onSaved: (p: Printer) => void;
}) {
  const models = useQuery({
    queryKey: ['printer-models'],
    queryFn: () => api.get('/printer-models', PrinterModelList),
    staleTime: Infinity,
  });
  const settings = useSettings();
  const [form, setForm] = useState<Form>(() => fromPrinter(printer ?? {}));
  const [touched, setTouched] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const price = parseCentavos(form.price);
  const lifetime = int(form.lifetime);
  const low = int(form.low);
  const high = int(form.high);
  const maintenance = parseCentavos(form.maintenance);
  const date = form.purchaseDate.trim() === '' ? null : parseBrDate(form.purchaseDate);
  const errors = {
    name: form.name.trim() === '' ? 'Dê um apelido à impressora.' : null,
    model: form.model.trim() === '' ? 'Informe o modelo.' : null,
    price: price === null ? 'Informe o preço pago.' : null,
    lifetime: lifetime === null || lifetime < 1 ? 'Informe a vida útil em horas.' : null,
    low: low === null ? 'Informe a potência.' : null,
    high: high === null ? 'Informe a potência.' : null,
    date: form.purchaseDate.trim() !== '' && date === null ? 'Use dd/mm/aaaa.' : null,
  };
  const valid = Object.values(errors).every((e) => e === null);

  const tariff = settings.data?.costs.energyTariffPerKwh ?? null;
  const surcharge = settings.data?.costs.bandeiraPerKwh ?? 0;
  const preview = (watts: number | null) =>
    price !== null && lifetime && watts !== null
      ? Math.round(
          price / lifetime +
            (tariff === null ? 0 : (watts / 1000) * (tariff + surcharge) * 100) +
            (maintenance ?? 0),
        )
      : null;

  const body: CreatePrinterBody = {
    name: form.name.trim(),
    model: form.model.trim(),
    purchaseDate: date,
    purchasePriceCentavos: price ?? 0,
    lifetimeHours: lifetime ?? 1,
    powerLowWatts: low ?? 0,
    powerHighWatts: high ?? 0,
    maintenancePerHourCentavos: maintenance ?? 0,
    buildVolume: textOrNull(form.buildVolume),
    nozzles: textOrNull(form.nozzles),
    multicolorUnit: textOrNull(form.multicolorUnit),
    multicolorSlots: int(form.multicolorSlots),
    enclosure: textOrNull(form.enclosure),
    materials: form.materials,
  };
  const save = useMutation({
    mutationFn: () =>
      printer
        ? api.patch(`/printers/${printer.id}`, Printer, body)
        : api.post('/printers', Printer, body, { idempotencyKey: key }),
    onSuccess: onSaved,
  });

  const err = (k: keyof typeof errors) => (touched ? errors[k] : null);
  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(min(180px,100%),1fr))',
    gap: 12,
  } as const;

  return (
    <Modal
      title={printer ? 'Editar impressora' : 'Nova impressora'}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTouched(true);
              if (valid) save.mutate();
            }}
          >
            Salvar
          </button>
        </>
      }
    >
      {!printer ? (
        <Field
          id="pm"
          label="Modelo conhecido"
          help="Escolha o modelo e os dados técnicos vêm preenchidos. Só confira preço e data."
        >
          <select
            id="pm"
            className="input"
            defaultValue=""
            onChange={(e) => {
              const model = models.data?.data.find((m) => m.id === e.target.value);
              if (!model) return;
              setForm((f) => ({
                ...fromPrinter({ ...model.defaults, name: f.name || model.name.split(' ').at(-1) }),
                purchaseDate: f.purchaseDate,
              }));
            }}
          >
            <option value="">Modelo personalizado</option>
            {models.data?.data.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.complete ? ' · dados completos' : ''}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <div style={grid}>
        <Field id="p-name" label="Apelido" error={err('name')}>
          <input
            id="p-name"
            autoComplete="off"
            className={err('name') ? 'input err' : 'input'}
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field id="p-model" label="Modelo" error={err('model')}>
          <input
            id="p-model"
            autoComplete="off"
            className={err('model') ? 'input err' : 'input'}
            value={form.model}
            onChange={(e) => set('model', e.target.value)}
          />
        </Field>
        <Field id="p-date" label="Data da compra" error={err('date')}>
          <input
            id="p-date"
            className={err('date') ? 'input err' : 'input'}
            placeholder="dd/mm/aaaa"
            value={form.purchaseDate}
            onChange={(e) => set('purchaseDate', e.target.value)}
          />
        </Field>
      </div>
      <div style={grid}>
        <Field id="p-price" label="Preço pago" error={err('price')}>
          <AffixInput
            id="p-price"
            prefix="R$"
            value={form.price}
            onChange={(v) => set('price', v)}
            invalid={!!err('price')}
          />
        </Field>
        <Field
          id="p-life"
          label="Vida útil esperada"
          error={err('lifetime')}
          help="Chute inicial. Troca pela média real quando uma impressora se aposenta."
        >
          <AffixInput
            id="p-life"
            unit="h"
            inputMode="numeric"
            value={form.lifetime}
            onChange={(v) => set('lifetime', v)}
            invalid={!!err('lifetime')}
          />
        </Field>
        <Field id="p-maint" label="Manutenção (estimada)">
          <AffixInput
            id="p-maint"
            prefix="R$"
            unit="/h"
            value={form.maintenance}
            onChange={(v) => set('maintenance', v)}
          />
        </Field>
      </div>
      <span className="lbl">Potência média por material</span>
      <div style={grid}>
        <Field id="p-low" label={POWER_GROUP_LABELS.low} error={err('low')}>
          <AffixInput
            id="p-low"
            unit="W"
            inputMode="numeric"
            value={form.low}
            onChange={(v) => set('low', v)}
            invalid={!!err('low')}
          />
        </Field>
        <Field id="p-high" label={POWER_GROUP_LABELS.high} error={err('high')}>
          <AffixInput
            id="p-high"
            unit="W"
            inputMode="numeric"
            value={form.high}
            onChange={(v) => set('high', v)}
            invalid={!!err('high')}
          />
        </Field>
      </div>
      <p className="xs muted">
        Dica: meça com uma tomada inteligente durante algumas impressões, em vez de usar a ficha
        técnica.
      </p>
      <span className="lbl">Capacidades</span>
      <div style={grid}>
        <Field id="p-vol" label="Volume de impressão">
          <input
            id="p-vol"
            className="input"
            placeholder="256 × 256 × 260 mm"
            value={form.buildVolume}
            onChange={(e) => set('buildVolume', e.target.value)}
          />
        </Field>
        <Field id="p-noz" label="Bicos">
          <input
            id="p-noz"
            className="input"
            placeholder="conferir na ficha técnica"
            value={form.nozzles}
            onChange={(e) => set('nozzles', e.target.value)}
          />
        </Field>
        <Field id="p-encl" label="Câmara">
          <input
            id="p-encl"
            className="input"
            placeholder="Aberta, fechada, aquecida…"
            value={form.enclosure}
            onChange={(e) => set('enclosure', e.target.value)}
          />
        </Field>
        <Field id="p-mcu" label="Multicolor">
          <input
            id="p-mcu"
            className="input"
            placeholder="Ex.: AMS 2 Pro"
            value={form.multicolorUnit}
            onChange={(e) => set('multicolorUnit', e.target.value)}
          />
        </Field>
        <Field id="p-mcs" label="Slots">
          <AffixInput
            id="p-mcs"
            inputMode="numeric"
            value={form.multicolorSlots}
            onChange={(v) => set('multicolorSlots', v)}
          />
        </Field>
      </div>
      <Field label="Materiais">
        <div className="row wrap" style={{ gap: 6 }}>
          {Material.options.map((m) => {
            const on = form.materials.includes(m);
            return (
              <button
                key={m}
                type="button"
                className={on ? 'chip on' : 'chip'}
                aria-pressed={on}
                onClick={() =>
                  set(
                    'materials',
                    on ? form.materials.filter((x) => x !== m) : [...form.materials, m],
                  )
                }
              >
                {m}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="card sunk col" style={{ gap: 4, padding: 14 }} aria-live="polite">
        <div className="kv">
          <span className="muted">Custo por hora · {POWER_GROUP_LABELS.low}</span>
          <span className="v">{preview(low) === null ? '—' : formatBRL(preview(low)!)}</span>
        </div>
        <div className="kv">
          <span className="muted">Custo por hora · {POWER_GROUP_LABELS.high}</span>
          <span className="v">{preview(high) === null ? '—' : formatBRL(preview(high)!)}</span>
        </div>
        <p className="xs muted">
          {tariff === null
            ? 'A tarifa de energia ainda não foi informada (Configurações · Custos); a energia entra quando ela existir.'
            : 'O cartão “Custo por hora” e os orçamentos novos usam estes valores.'}
        </p>
      </div>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      {touched && !valid ? <Alert tone="err">Confira os campos marcados.</Alert> : null}
    </Modal>
  );
}
